import { parseWithZod } from '@conform-to/zod/v4'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { useEffect } from 'react'
import {
	type ActionFunctionArgs,
	data,
	type MetaFunction,
	useFetcher,
} from 'react-router'
import { toast as showToast } from 'sonner'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion'
import { sendEmail } from '#app/utils/email.server.ts'

const ContactFormSchema = z.object({
	name: z.string().min(1, 'Name is required'),
	email: z.string().email('Please enter a valid email address'),
	subject: z.string().min(1, 'Subject is required'),
	message: z.string().min(10, 'Message must be at least 10 characters long'),
	gjFrncbm: z.string().optional(),
})

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/support', priority: 0.7 }],
}

export const meta: MetaFunction = ({ location }) => {
	const title = `Support | ${import.meta.env.VITE_APP_TITLE}`
	const description = import.meta.env.VITE_APP_DESCRIPTION
	const url = `https://www.trailviewr.com${location.pathname}`

	return [
		{ title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: url },

		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: url },

		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:url', content: url },
	]
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, { schema: ContactFormSchema })

	if (submission.status !== 'success') {
		return data({ result: submission.error }, { status: 400 })
	}

	const { email, name, subject, message } = submission.value

	const textMessage = `${import.meta.env.VITE_APP_NAME} Contact Form
  Name: ${name}
  Email: ${email}
  Subject: ${subject}
  Message: ${message}
  `

	const htmlMessage = `<h1>${import.meta.env.VITE_APP_TITLE} Contact Form</h1>
  <p><strong>Name:</strong> ${name}</p>
  <p><strong>Email:</strong> ${email}</p>
  <p><strong>Subject:</strong> ${subject}</p>
  <p><strong>Message:</strong> ${message}</p>`

	try {
		const response = await sendEmail({
			to: process.env.CONTACT_FORM_EMAIL_RECEIVER || 'contact@trailviewr.com',
			subject: `Contact Form Submission from ${email}`,
			text: textMessage,
			html: htmlMessage,
		})

		if (response.status === 'success') {
			return data({ result: 'success' }, 200)
		} else {
			return data(
				{ result: 'Email failed to send.' },
				response.error.statusCode || 500,
			)
		}
	} catch (error) {
		console.error(error)
		return data({ result: 'Error sending email.' }, 500)
	}
}

export default function SupportRoute() {
	const fetcher = useFetcher<typeof action>()

	useEffect(() => {
		if (fetcher.state === 'idle' && fetcher.data?.result === 'success') {
			showToast.success('Contact Form', {
				description: 'Your message has been submitted successfully!',
			})
		}
	}, [fetcher.state, fetcher.data])

	return (
		<main
			id="main"
			className="w-full pb-2 md:pb-4 lg:container lg:mx-auto lg:pb-6"
		>
			<section id="faq" className="pb-8 md:pb-16 lg:pb-24">
				<h2 className="mb-8 text-center text-3xl font-semibold">
					Frequently Asked Questions
				</h2>
				<Accordion type="single" collapsible className="w-full">
					<AccordionItem value="item-1">
						<AccordionTrigger>
							What types of trails are covered on the platform?
						</AccordionTrigger>
						<AccordionContent>
							Our platform covers a wide range of trails including those for
							hiking, biking, camping, climbing, snowsports, walking, running,
							or backpacking.
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value="item-2">
						<AccordionTrigger>
							How current are the images and videos of the trails?
						</AccordionTrigger>
						<AccordionContent>
							Our platform aims to regularly update the 360-degree images and
							videos to reflect seasonal changes. Users can also upload their
							own 360-degree videos to keep the content fresh and current.
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value="item-3">
						<AccordionTrigger>
							What do you mean by "immersive experience"?
						</AccordionTrigger>
						<AccordionContent>
							The platform offers 360-degree imagery and videos of trails in
							every season, allowing for an authentic preview of what to expect.
							Additionally, our content is compatible with VR headsets, enabling
							you to experience the trails in a truly immersive way before you
							set foot on them.
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value="item-4">
						<AccordionTrigger>
							Can I experience the trails in Virtual Reality (VR) or Augmented
							Reality (AR)?
						</AccordionTrigger>
						<AccordionContent>
							Yes, our platform is compatible with VR and AR devices, offering
							an incredibly immersive way to explore trails. You can even sync
							your experience with friends, family, or colleagues, each
							exploring the trail in their own viewpoint.
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value="item-5">
						<AccordionTrigger>
							Is the platform accessible for people with mobility issues or
							other conditions?
						</AccordionTrigger>
						<AccordionContent>
							Absolutely, our platform aims to make the great outdoors
							accessible for everyone, including those with mobility issues,
							fear of heights, or agoraphobia. Our VR/AR features allow for a
							fully immersive experience from the comfort and safety of your
							home.
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value="item-6">
						<AccordionTrigger>
							How does the platform personalize trail recommendations?
						</AccordionTrigger>
						<AccordionContent>
							We employ Artificial Intelligence (AI) and machine learning (ML)
							algorithms that take into account your past activity, preferences,
							and real-time conditions to provide personalized trail
							recommendations.
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value="item-7">
						<AccordionTrigger>Is the platform free to use?</AccordionTrigger>
						<AccordionContent>
							We're still in the planning stage and haven't finalized our
							pricing model yet. However, users who contribute their own
							360-degree videos of trails will be eligible for a free or
							reduced-cost membership level.
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value="item-8">
						<AccordionTrigger>
							How can I or my company become a sponsor?
						</AccordionTrigger>
						<AccordionContent>
							We're actively seeking partnerships with like-minded organizations
							and individuals who share our vision for revolutionizing outdoor
							exploration. Please contact us for sponsorship opportunities.
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</section>

			{/* Contact Form Section */}
			{/*<section id="contact" className="container mx-auto px-4 py-16">*/}
			{/*	<h2 className="text-3xl font-semibold text-center mb-8">Contact Us</h2>*/}
			{/*	<div className="grid grid-cols-1 md:grid-cols-2 gap-8">*/}
			{/*		<div>*/}
			{/*			<fetcher.Form method="post" {...getFormProps(form)} className="space-y-4">*/}
			{/*				<HoneypotInputs />*/}
			{/*				<Field*/}
			{/*					labelProps={{ htmlFor: fields.name.id, children: 'Your full name' }}*/}
			{/*					inputProps={{*/}
			{/*						...getInputProps(fields.name, { type: 'text' }),*/}
			{/*						autoFocus: true,*/}
			{/*						className: 'form-input w-full px-4 py-2 rounded-lg border',*/}
			{/*					}}*/}
			{/*					errors={fields.name.errors}*/}
			{/*				/>*/}
			{/*				<Field*/}
			{/*					labelProps={{ htmlFor: fields.email.id, children: 'Email' }}*/}
			{/*					inputProps={{*/}
			{/*						...getInputProps(fields.email, { type: 'email' }),*/}
			{/*						className: 'form-input w-full px-4 py-2 rounded-lg border',*/}
			{/*					}}*/}
			{/*					errors={fields.email.errors}*/}
			{/*				/>*/}
			{/*				<Field*/}
			{/*					labelProps={{ htmlFor: fields.subject.id, children: 'Subject' }}*/}
			{/*					inputProps={{*/}
			{/*						...getInputProps(fields.subject, { type: 'text' }),*/}
			{/*						className: 'form-input w-full px-4 py-2 rounded-lg border',*/}
			{/*					}}*/}
			{/*					errors={fields.subject.errors}*/}
			{/*				/>*/}
			{/*				<TextareaField*/}
			{/*					labelProps={{ htmlFor: fields.message.id, children: 'Message' }}*/}
			{/*					textareaProps={{*/}
			{/*						rows: 5,*/}
			{/*						...getTextareaProps(fields.message),*/}
			{/*						className: 'form-input w-full px-4 py-2 rounded-lg border',*/}
			{/*					}}*/}
			{/*					errors={fields.message.errors}*/}
			{/*				/>*/}
			{/*				<ErrorList errors={form.errors} id={form.errorId} />*/}
			{/*				<StatusButton*/}
			{/*					status={fetcher.state === 'submitting' ? 'pending' : 'idle'}*/}
			{/*					type="submit"*/}
			{/*					className="w-full py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"*/}
			{/*				>*/}
			{/*					Submit*/}
			{/*				</StatusButton>*/}
			{/*			</fetcher.Form>*/}
			{/*		</div>*/}
			{/*	</div>*/}
			{/*</section>*/}
		</main>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
